/* ELIO · one-item checkout nudge
   One global checkout gate for every checkout surface.
   Exactly one cart item => compact complete-the-pair nudge.
   Two or more => checkout immediately. Test mode can force the nudge on any non-empty cart.
*/
(() => {
  if (window.__ELIO_CHECKOUT_NUDGE_LOADED__) return;
  window.__ELIO_CHECKOUT_NUDGE_LOADED__ = true;

  const DISCOUNT_KEY = 'elio_active_discount_code';
  const cfgEl = () => document.getElementById('elio-checkout-nudge-config');
  let busy = false;
  let bypassOnce = false;
  let catalogPromise = null;

  function config() {
    const el = cfgEl();
    if (!el) return { enabled: false };
    try { return JSON.parse(el.textContent || '{}'); } catch (e) { return { enabled: false }; }
  }

  function discountCode() {
    const el = document.querySelector('[data-discount-code]');
    const dom = el ? (el.getAttribute('data-discount-code') || '').trim() : '';
    if (dom) { try { localStorage.setItem(DISCOUNT_KEY, dom); } catch (e) {} return dom; }
    try { return localStorage.getItem(DISCOUNT_KEY) || ''; } catch (e) { return ''; }
  }

  function checkoutUrl() {
    const code = discountCode();
    return code ? '/discount/' + encodeURIComponent(code) + '?redirect=/checkout' : '/checkout';
  }

  function goCheckout(opts = {}) {
    const url = checkoutUrl();
    if (opts.sizeGateApproved) {
      window.location.assign(url);
      return;
    }
    if (window.elioSizeGate && typeof window.elioSizeGate.guard === 'function') {
      window.elioSizeGate.guard(() => window.location.assign(url));
    } else {
      window.location.assign(url);
    }
  }

  async function getCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error('cart read failed');
    return r.json();
  }

  function sizeFromItem(item) {
    const ovs = Array.isArray(item?.options_with_values) ? item.options_with_values : [];
    const named = ovs.find(o => String(o?.name || '').toLowerCase() === 'size');
    if (named) return String(named.value || '').trim();
    if (ovs.length === 1) return String(ovs[0]?.value || '').trim();
    const opts = Array.isArray(item?.variant_options) ? item.variant_options : [];
    return opts.length ? String(opts[opts.length - 1] || '').trim() : '';
  }

  function productHandleFromItem(item) {
    if (item?.handle) return item.handle;
    const url = String(item?.url || '');
    const m = url.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function cleanColor(title) {
    return String(title || '')
      .replace(/classic/ig, '').replace(/flagship/ig, '').replace(/brief/ig, '')
      .replace(/men'?s/ig, '').replace(/underwear/ig, '').replace(/[()]/g, '')
      .replace(/[-–—]/g, ' ').replace(/pine green/ig, 'Pine').replace(/navy blue/ig, 'Navy')
      .replace(/brown/ig, 'Espresso').replace(/nude/ig, 'Tan').replace(/\s+/g, ' ').trim() || 'Brief';
  }

  // /collections/x/products.json returns variant.price as a decimal STRING ("22.00")
  // while /products/handle.js returns integer CENTS. Normalise both to cents so the
  // bundle maths downstream can't silently compare 22 against 2200.
  function toCents(v) {
    if (v == null || v === '') return 0;
    if (typeof v === 'number') return Math.round(v);
    const n = Number(String(v).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function normalizeProducts(raw) {
    const products = Array.isArray(raw?.products) ? raw.products : [];
    return products.filter(p => Array.isArray(p.variants) && p.variants.length).map(p => {
      const tags = Array.isArray(p.tags)
        ? p.tags.map(x => String(x).trim().toLowerCase())
        : String(p.tags || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean);
      return {
      id: p.id,
      title: p.title,
      handle: p.handle,
      presale: tags.includes('presale'),
      family: (tags.includes('bamboo-cotton') || /bamboo[ -]cotton/i.test((p.title || '') + ' ' + (p.handle || ''))) ? 'bamboo' : 'original',
      presaleShip: tags.includes('presale') ? 'Ships by October 29' : '',
      image: Array.isArray(p.images) && p.images[0] ? (typeof p.images[0] === 'string' ? p.images[0] : (p.images[0].src || '')) : (p.image?.src || ''),
      options: p.options || [],
      variants: p.variants.map(v => ({
        id: v.id,
        available: v.available !== false,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        title: v.title,
        price: toCents(v.price)
      }))
    };
    });
  }

  async function loadCatalog(cart) {
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
      const c = config();
      const handle = String(c.collectionHandle || '').trim();
      if (handle) {
        try {
          const r = await fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=50', { credentials: 'same-origin' });
          if (r.ok) {
            const list = normalizeProducts(await r.json());
            if (list.length) return list;
          }
        } catch (e) {}
      }
      try {
        const currentHandle = productHandleFromItem(cart?.items?.[0]);
        if (!currentHandle) return [];
        const r = await fetch('/products/' + encodeURIComponent(currentHandle) + '.js', { credentials: 'same-origin' });
        if (!r.ok) return [];
        const p = await r.json();
        const tags = Array.isArray(p.tags)
          ? p.tags.map(x => String(x).trim().toLowerCase())
          : String(p.tags || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean);
        return [{
          id: p.id, title: p.title, handle: p.handle,
          presale: tags.includes('presale'),
          presaleShip: tags.includes('presale') ? 'Ships by October 29' : '',
          image: p.featured_image || (Array.isArray(p.images) ? p.images[0] : ''),
          options: p.options || [],
          variants: (p.variants || []).map(v => ({ id:v.id, available:v.available !== false, option1:v.option1, option2:v.option2, option3:v.option3, title:v.title, price: toCents(v.price) }))
        }];
      } catch (e) { return []; }
    })();
    return catalogPromise;
  }

  function sizeOptionIndex(product) {
    const opts = Array.isArray(product?.options) ? product.options : [];
    const idx = opts.findIndex(o => String(o?.name || o || '').toLowerCase() === 'size');
    if (idx >= 0) return idx;
    return opts.length === 1 ? 0 : Math.max(0, opts.length - 1);
  }

  function variantSize(product, variant) {
    const idx = sizeOptionIndex(product);
    return String(variant?.['option' + (idx + 1)] || '').trim();
  }

  function availableSizes(product) {
    const seen = new Set();
    const out = [];
    (product?.variants || []).forEach(v => {
      if (!v.available) return;
      const s = variantSize(product, v);
      if (s && !seen.has(s)) { seen.add(s); out.push(s); }
    });
    return out;
  }

  function variantFor(product, size) {
    const variants = product?.variants || [];
    return variants.find(v => v.available && (!size || variantSize(product, v) === size)) || variants.find(v => v.available) || null;
  }

  function colorKey(text) {
    const t = String(text || '').toLowerCase();
    if (t.includes('tan') || t.includes('nude')) return 'tan';
    if (t.includes('espresso') || t.includes('brown')) return 'espresso';
    if (t.includes('pine') || t.includes('green')) return 'pine';
    if (t.includes('navy') || t.includes('blue')) return 'navy';
    if (t.includes('black')) return 'black';
    if (t.includes('crimson') || t.includes('red')) return 'crimson';
    return '';
  }

  const COLOR_META = {
    crimson: { label: 'Crimson', swatch: '#6F0A25' },
    navy: { label: 'Navy', swatch: '#152230' },
    black: { label: 'Black', swatch: '#0B0B0B' },
    tan: { label: 'Tan', swatch: '#DAB39E' },
    espresso: { label: 'Espresso', swatch: '#4C1E0E' },
    pine: { label: 'Pine', swatch: '#5C6F62' }
  };

  /* ---------------------------------------------------------------
     ELIGIBLE BRIEFS, not raw cart units.

     The pair rule applies to briefs. cart.item_count counts everything,
     so the moment a non-brief exists (gift card, packaging, anything)
     a cart of 2 briefs + 1 other would read as odd and the copy would
     contradict the discount Shopify actually applied.

     An item is eligible if its colourway resolves to a known ELIO
     colour. Today that is every product, so behaviour is unchanged;
     it just stops being an accident.
     --------------------------------------------------------------- */
  function isEligibleBrief(item) {
    if (!item) return false;
    const text = [item.product_title, item.title, productHandleFromItem(item)]
      .filter(Boolean).join(' ');
    const normalized = text.toLowerCase();
    if (!normalized.includes('brief')) return false;

    const key = colorKey(text);
    return !!(key && COLOR_META[key]);
  }

  function eligibleItems(cart) {
    return ((cart && cart.items) || []).filter(isEligibleBrief);
  }

  function itemFamily(item) {
    if (window.ElioOffer && ElioOffer.familyKey) return ElioOffer.familyKey(item);
    const text = [item?.product_title, item?.title, productHandleFromItem(item)].filter(Boolean).join(' ');
    return /bamboo/i.test(text) ? 'bamboo' : 'original';
  }

  /* Units are counted within fabric family. Original and Bamboo never complete
     each other's offer pair because they have independent Theme Editor controls. */
  function eligibleUnitCount(cart, family) {
    return eligibleItems(cart).filter(it => !family || itemFamily(it) === family)
      .reduce((n, it) => n + (Number(it.quantity) || 0), 0);
  }

  const ELIO_NUDGE_MAX_UNITS = 3;

  function familyMode(family) {
    if (window.ElioOffer && ElioOffer.familyConfig) return ElioOffer.familyConfig(family).mode;
    const c = config();
    return family === 'bamboo' ? (c.bambooOfferMode || 'bundle') : (c.originalOfferMode || 'bundle');
  }

  function incompleteFamilies(cart) {
    return ['original','bamboo'].filter(family => {
      if (familyMode(family) === 'off') return false;
      const n = eligibleUnitCount(cart, family);
      return n > 0 && n % 2 === 1 && n <= ELIO_NUDGE_MAX_UNITS;
    });
  }

  function hasIncompletePair(cart) { return incompleteFamilies(cart).length > 0; }

  function complementFor(key) {
    return ({ tan:'espresso', espresso:'tan', pine:'espresso', navy:'black', black:'crimson', crimson:'black' })[key] || 'black';
  }

  function ensureStyles() {
    if (document.getElementById('elio-one-nudge-styles')) return;
    const s = document.createElement('style');
    s.id = 'elio-one-nudge-styles';
    s.textContent = `
      .elio-one-nudge{--ink:#eae6de;--ink-bright:#f6f3ec;--line:rgba(255,255,255,.14);--gold:#ff5964;--crimson:#a8434d;--crimson-live:#c2505c;position:fixed;inset:0;z-index:2147483647;display:flex;align-items:flex-end;justify-content:center;font-family:Avenir,"Avenir Next",-apple-system,BlinkMacSystemFont,"Helvetica Neue",Arial,sans-serif}
      .elio-one-nudge[hidden]{display:none!important}
      /* Backdrop stays light enough that backdrop-filter has something to frost.
         An opaque scrim leaves nothing behind the sheet and the glass reads flat. */
      .elio-one-nudge__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.44)}
      /* Height is capped to the VISIBLE viewport. dvh is used where supported because
         vh on a phone counts the area behind the browser chrome, which is exactly how
         a sheet ends up taller than the screen it's sitting in. The sheet is a flex
         column: the content scrolls, the buttons stay pinned and always reachable. */
      .elio-one-nudge__sheet{position:relative;width:100%;max-width:520px;max-height:100vh;display:flex;flex-direction:column;overflow:hidden;color:var(--ink);border:1px solid rgba(168,67,77,.2);border-top:1px solid rgba(168,67,77,.4);border-bottom:0;padding:26px 22px calc(20px + env(safe-area-inset-bottom));animation:elioOnePcUp .3s cubic-bezier(.16,1,.3,1);background:linear-gradient(180deg,rgba(255,255,255,.07) 0%,rgba(255,255,255,0) 14%),linear-gradient(180deg,rgba(26,16,18,.72) 0%,rgba(15,12,13,.62) 100%);box-shadow:inset 0 1px 0 rgba(255,255,255,.1),0 -24px 70px rgba(0,0,0,.5);-webkit-backdrop-filter:blur(20px) saturate(1.12);backdrop-filter:blur(20px) saturate(1.12)}
      @supports (max-height:100dvh){.elio-one-nudge__sheet{max-height:100dvh}}

      /* min-height:0 is required — without it a flex child refuses to shrink below its
         content and the overflow silently pushes the footer off-screen instead. */
      .elio-one-nudge__scroll{flex:1 1 auto;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;scrollbar-width:thin}
      .elio-one-nudge__scroll::-webkit-scrollbar{width:4px}
      .elio-one-nudge__scroll::-webkit-scrollbar-thumb{background:rgba(255,255,255,.2);border-radius:4px}
      .elio-one-nudge__foot{flex:0 0 auto;padding-top:12px;margin-top:4px;border-top:1px solid rgba(255,255,255,.1)}
      @keyframes elioOnePcUp{from{transform:translateY(100%)}to{transform:none}}
      @media(min-width:760px){
        .elio-one-nudge{align-items:center;padding:24px}
        .elio-one-nudge__sheet{max-height:min(640px, calc(100vh - 48px));padding:24px 24px 20px;border-bottom:1px solid rgba(168,67,77,.2)}
        /* Five colours across two columns is three tall rows. Three columns fits them
           in two and takes ~90px of height out of the dialog. */
        .elio-one-nudge__swatches{grid-template-columns:repeat(3,minmax(0,1fr))}
      }
      @media(max-height:720px){
        .elio-one-nudge__sheet{padding:20px 18px calc(16px + env(safe-area-inset-bottom))}
        .elio-one-nudge__title{font-size:clamp(19px,4.4vw,23px)}
        .elio-one-nudge__sub{margin-bottom:12px;font-size:13px}
        .elio-one-nudge__swatch{min-height:66px;padding:10px 12px}
        .elio-one-nudge__add,.elio-one-nudge__clone{min-height:50px}
        .elio-one-nudge__skip{margin-top:8px;padding:8px}
      }
      .elio-one-nudge__close{position:absolute;top:12px;right:12px;z-index:2;display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;padding:0;border:1px solid rgba(255,255,255,.16);border-radius:50%;background:rgba(255,255,255,.05);color:var(--ink);font-family:inherit;font-size:14px;line-height:1;cursor:pointer;-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);transition:border-color .18s ease,background .18s ease,color .18s ease}
      .elio-one-nudge__close:hover{border-color:rgba(255,255,255,.45);background:rgba(255,255,255,.1);color:#fff}
      .elio-one-nudge__kicker{margin:0 0 8px;font-size:10px;letter-spacing:.28em;text-transform:uppercase;color:var(--gold)}
      .elio-one-nudge__title{margin:0 0 6px;font-size:clamp(20px,4.6vw,24px);font-weight:300;line-height:1.15;letter-spacing:.02em;color:var(--ink-bright);padding-right:34px}
      .elio-one-nudge__sub{margin:0 0 14px;font-size:13.5px;line-height:1.45;color:rgba(234,230,222,.72)}
      /* Sits directly above the colours so the size reads as a property of the whole
         set. Saves saying "in your size" in the body copy. */
      .elio-one-nudge__size{display:inline-block;margin:0 0 9px;padding:4px 9px;border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.05);font-size:9.5px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:rgba(234,230,222,.72);white-space:nowrap}
      .elio-one-nudge__swatches{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin:0 0 4px}
      /* Two columns can't hold a colour name and two price rows on a small phone.
         Below this width the grid drops to one column rather than compressing. */
      @media (max-width:430px){.elio-one-nudge__swatches{grid-template-columns:minmax(0,1fr)}}
      .elio-one-nudge__swatch{display:flex;align-items:center;gap:10px;padding:10px 12px;background:rgba(255,255,255,.045);border:1px solid rgba(255,255,255,.14);box-shadow:inset 0 1px 0 rgba(255,255,255,.06);cursor:pointer;font-family:inherit;text-align:left;position:relative;transition:border-color .18s ease,background .18s ease,transform .18s ease}
      /* Hover is a lift only. It must not borrow anything from the selected state,
         or a pointer resting on a tile reads as a choice already made. */
      .elio-one-nudge__swatch:hover{background:rgba(255,255,255,.075);border-color:rgba(255,255,255,.4);transform:translateY(-1px)}
      /* Selected is lit crimson glass with a bright top rim — earned by tapping. */
      .elio-one-nudge__swatch[aria-checked="true"]{background:linear-gradient(180deg,rgba(173,28,58,.34),rgba(92,13,31,.3));border-color:rgba(168,67,77,.55);border-top-color:#a8434d;box-shadow:inset 0 1px 0 rgba(255,255,255,.22),inset 0 0 22px rgba(248,72,108,.14);transform:none}
      /* The recommendation is a label, not a state. It gets no border treatment. */
      .elio-one-nudge__swatch.is-complement{border-color:rgba(255,255,255,.14)}
      .elio-one-nudge__swatch-dot{width:18px;height:18px;border-radius:50%;background:var(--sw);border:1px solid rgba(255,255,255,.3);box-shadow:inset 0 1px 0 rgba(255,255,255,.22);flex:0 0 auto}
      .elio-one-nudge__swatch[aria-checked="true"] .elio-one-nudge__swatch-dot{box-shadow:0 0 0 2px rgba(15,12,13,.9),0 0 0 4px #ff5964}
      .elio-one-nudge__swatch-text{display:flex;flex-direction:column;gap:2px;min-width:0;flex:1 1 auto}
      .elio-one-nudge__swatch-name{font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--ink)}
      /* The theme sets a global word-break:break-word on every element, which will happily
         split "$20" into "$2" and "0" inside a narrow tile. Every descendant of the
         sheet is reset, and anything carrying a figure is additionally pinned to
         nowrap. A price must never be allowed to break. */
      .elio-one-nudge *{word-break:normal !important;overflow-wrap:normal;hyphens:none}
      .elio-one-nudge__swatch-price,
      .elio-one-nudge__swatch-price *,
      .elio-one-nudge__swatch-other-prices,
      .elio-one-nudge__swatch-other-prices *,
      .elio-one-nudge__math{white-space:nowrap}

      .elio-one-nudge__swatch-price{display:block;font-size:11px;letter-spacing:.04em;color:rgba(234,230,222,.6)}
      .elio-one-nudge__swatch-price s{opacity:1;color:rgba(234,230,222,.72);text-decoration-color:#ff5964;text-decoration-thickness:1.5px}
      .elio-one-nudge__swatch-price b{font-weight:600;color:#ff8f98}
      .elio-one-nudge__swatch[aria-checked="true"] .elio-one-nudge__swatch-price{color:rgba(255,246,244,.72)}
      .elio-one-nudge__swatch[aria-checked="true"] .elio-one-nudge__swatch-price b{color:#ffb3ba}
      /* The "your CRIMSON drops to X" line. Quieter than the tile's own price so it
         reads as a consequence, not as this colour's price. */
      /* Label above, figures below. Two rows means the label can wrap freely without
         ever dragging a number onto a new line with it. */
      .elio-one-nudge__swatch-other{display:block;margin-top:4px;padding-top:4px;border-top:1px solid rgba(255,255,255,.1);font-size:9.5px;line-height:1.35;letter-spacing:.08em;text-transform:uppercase;color:rgba(234,230,222,.62)}
      .elio-one-nudge__swatch-other-label{display:block}
      .elio-one-nudge__swatch-other-prices{display:block;margin-top:2px;font-size:10.5px;letter-spacing:.02em}
      .elio-one-nudge__swatch-other s{opacity:1;color:rgba(234,230,222,.7);text-decoration-color:#ff5964;text-decoration-thickness:1.5px}
      .elio-one-nudge__swatch-other b{font-weight:600;color:#ff8f98}
      .elio-one-nudge__swatch[aria-checked="true"] .elio-one-nudge__swatch-other{color:rgba(255,246,244,.6)}
      .elio-one-nudge__swatch[aria-checked="true"] .elio-one-nudge__swatch-other b{color:#ffb3ba}
      /* Sits between the colours and the button. Empty until a colour is picked, and
         it reserves its own height so the sheet doesn't jump when it fills. */
      .elio-one-nudge__math{min-height:17px;margin:0;font-size:12px;line-height:1.4;letter-spacing:.02em;color:#ffb3ba;text-align:center}
      .elio-one-nudge__math:empty{margin-top:4px}
      .elio-one-nudge__swatch[aria-checked="true"] .elio-one-nudge__swatch-name{color:#fff6f4}
      .elio-one-nudge__swatch-tag{position:absolute;top:-8px;right:8px;background:rgba(15,12,13,.92);color:rgba(234,230,222,.8);border:1px solid rgba(255,255,255,.18);font-size:8px;letter-spacing:.08em;text-transform:uppercase;padding:2px 6px;line-height:1}
      .elio-one-nudge__swatches.is-busy{opacity:.5;pointer-events:none}
      .elio-one-nudge__add,.elio-one-nudge__add:hover,.elio-one-nudge__add:focus,
      .elio-one-nudge__clone,.elio-one-nudge__clone:hover,.elio-one-nudge__clone:focus{display:block;width:100%;border:0;background:linear-gradient(180deg,#ad1c3a,#8c1430 50%,#5c0d1f);color:#fff6f4;text-shadow:0 1px 1px rgba(0,0,0,.22);box-shadow:inset 0 1px 0 rgba(255,255,255,.3),inset 0 0 24px rgba(248,72,108,.2),inset 0 -13px 26px rgba(60,7,22,.5),inset 0 -1px 0 rgba(0,0,0,.34);min-height:50px;font-family:inherit;font-size:12px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;cursor:pointer;transition:filter .2s ease,background .2s ease,box-shadow .2s ease}
      .elio-one-nudge__add{margin-top:10px}
      .elio-one-nudge__add:hover:not([disabled]),.elio-one-nudge__clone:hover{filter:brightness(1.08)}
      /* Disabled is the resting state: nothing is chosen on open. It has to read as
         "not yet", not as a dimmed version of a live button. */
      .elio-one-nudge__add[disabled],.elio-one-nudge__add[disabled]:hover{cursor:default;background:rgba(255,255,255,.07);color:rgba(234,230,222,.5);text-shadow:none;box-shadow:inset 0 0 0 1px rgba(255,255,255,.14);filter:none}
      .elio-one-nudge__skip{display:block;width:100%;margin-top:8px;background:transparent;border:0;color:rgba(234,230,222,.6);font-family:inherit;font-size:11px;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;padding:8px;text-decoration:underline;text-underline-offset:3px}
      .elio-one-nudge__skip:hover{color:var(--ink-bright)}
      .elio-one-nudge__status{min-height:0;margin:8px 0 0;font-size:11px;color:#ffb3ba}
    `;
    document.head.appendChild(s);
  }

  function money(cents, currency, locale) {
    const amount = (Number(cents) || 0) / 100;
    const whole = Math.abs(amount % 1) < 0.005;
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(amount);
    } catch (e) {
      return '$' + (whole ? amount.toFixed(0) : amount.toFixed(2));
    }
  }

  /* Family-aware projection. Bundle and BOGO use the same central engine as
     the PDP and shipping projection; Shopify totals remain authoritative. */
  function bundleMath(cartUnitCents, candidateCents, family) {
    const a = Number(cartUnitCents) || 0;
    const b = Number(candidateCents) || 0;
    if (!a || !b) return null;
    const full = a + b;
    let savings = 0;
    if (window.ElioOffer && ElioOffer.pairDiscount) savings = ElioOffer.pairDiscount(a, b, family);
    else {
      const c = config();
      const mode = family === 'bamboo' ? (c.bambooOfferMode || 'bundle') : (c.originalOfferMode || 'bundle');
      if (mode === 'off') savings = 0;
      else if (mode === 'bogo_percent') {
        const selected = family === 'bamboo' ? c.bambooBogoPercent : c.originalBogoPercent;
        const pct = Number(selected != null ? selected : 50);
        savings = Math.round(Math.min(a,b) * pct / 100);
      } else {
        const selected = family === 'bamboo' ? c.bambooBundleDiscountCents : c.pairDiscountCents;
        savings = Number(selected != null ? selected : 1000);
      }
    }
    savings = Math.min(full, Math.max(0, savings));
    return { savings: savings, total: Math.max(0, full - savings) };
  }

  async function addVariant(id, presaleValue) {
    const payload = { id: Number(id), quantity: 1 };
    if (presaleValue) payload.properties = { Presale: presaleValue };
    const r = await fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type':'application/json', Accept:'application/json' },
      body: JSON.stringify(payload)
    });
    if (!r.ok) throw new Error('add failed');
    ['cart:update','cart:updated','cart:change','cart:refresh'].forEach(name => document.dispatchEvent(new CustomEvent(name, { bubbles:true })));
  }

  async function showNudge(cart) {
    ensureStyles();
    const c = config();
    const products = await loadCatalog(cart);
    /* Anchor on the LAST eligible brief rather than items[0]: in a 3-item
       cart the unpaired brief is the one the shopper just added, and the
       colour suggestions should complement that, not the first thing they
       ever put in the bag. */
    const eligible = eligibleItems(cart);
    const incomplete = incompleteFamilies(cart);
    const item = [...eligible].reverse().find(it => incomplete.includes(itemFamily(it))) || null;
    if (!item || !products.length) { goCheckout(); return; }
    const currentFamily = itemFamily(item);

    const currentSize = sizeFromItem(item);
    const currentHandle = productHandleFromItem(item);
    const currentProduct = products.find(p => p.handle === currentHandle);
    const currentKey = colorKey((currentProduct?.title || '') + ' ' + (item.product_title || '') + ' ' + currentHandle);
    const complement = complementFor(currentKey);

    const choices = [];
    products.forEach(product => {
      const key = colorKey((product.title || '') + ' ' + (product.handle || ''));
      if (!key || key === currentKey || !COLOR_META[key]) return;
      if ((product.family || 'original') !== currentFamily) return;
      const variant = currentSize ? (product.variants || []).find(v => v.available && variantSize(product, v) === currentSize) : variantFor(product, '');
      if (!variant) return;
      if (choices.some(x => x.key === key)) return;
      choices.push({ key, product, variant, price: Number(variant.price) || 0, ...COLOR_META[key] });
    });

    const cartUnit = Number(item.final_price != null ? item.final_price : item.price) || 0;
    const currency = cart?.currency || cart?.currency_code || 'USD';
    const locale = (document.documentElement.lang || 'en').trim();

    let selectedVariant = null;
    let selectedLabel = '';
    let selectedPrice = 0;
    const overlay = document.createElement('div');
    overlay.className = 'elio-one-nudge';
    overlay.setAttribute('role','dialog');
    overlay.setAttribute('aria-modal','true');
    const offerHeading = (window.ElioOffer && ElioOffer.offerLabel) ? ElioOffer.offerLabel(currentFamily) : (c.heading || 'Make it a pair?');
    const offerBody = (window.ElioOffer && ElioOffer.offerLabel) ? ElioOffer.offerLabel(currentFamily, 'add_one') : (c.body || 'Add one more.');
    overlay.setAttribute('aria-label', offerHeading || 'Complete your offer'); // fallback only fires if the config block is missing entirely

    /* Per-tile price. Under the flat pair rule no individual brief is
       "the discounted one", so a tile simply shows its own price and the
       pair saving is stated once, in the footer. That removes the old
       two-different-mechanics explanation entirely. */
    function swatchPriceHtml(ch) {
      if (!ch.price) return '';
      return `<span class="elio-one-nudge__swatch-price">${escapeHtml(money(ch.price, currency, locale))}</span>`;
    }

    const swatchHtml = choices.length ? choices.map(ch => `
      <button type="button" class="elio-one-nudge__swatch${ch.key === complement ? ' is-complement' : ''}" role="radio" aria-checked="false" data-variant="${ch.variant.id}" data-label="${escapeAttr(ch.label)}" data-price="${ch.price || 0}" style="--sw:${ch.swatch}" aria-label="${escapeAttr(ch.label)}${currentSize ? ', size ' + escapeAttr(currentSize) : ''}">
        <span class="elio-one-nudge__swatch-dot"></span>
        <span class="elio-one-nudge__swatch-text">
          <span class="elio-one-nudge__swatch-name">${escapeHtml(ch.label)}</span>
          ${swatchPriceHtml(ch)}
        </span>
        ${ch.key === complement ? `<span class="elio-one-nudge__swatch-tag">${escapeHtml(c.pairsWell || 'Goes well')}</span>` : ''}
      </button>`).join('') : '';

    const cloneVariant = Number(item.variant_id || item.id || 0);
    overlay.innerHTML = `
      <div class="elio-one-nudge__backdrop" data-close></div>
      <div class="elio-one-nudge__sheet" role="document">
        <button type="button" class="elio-one-nudge__close" data-close aria-label="Close">&#10005;</button>
        <div class="elio-one-nudge__scroll">
          <p class="elio-one-nudge__kicker">${escapeHtml(c.eyebrow || 'Before checkout')}</p>
          <h3 class="elio-one-nudge__title">${escapeHtml(offerHeading || 'Complete your offer')}</h3>
          <p class="elio-one-nudge__sub">${escapeHtml(offerBody || 'Add one more.')}</p>
          ${choices.length && currentSize ? `<p class="elio-one-nudge__size">${escapeHtml((c.sizeLabel || 'Size [size]').replace('[size]', currentSize))}</p>` : ''}
          ${choices.length ? `<div class="elio-one-nudge__swatches" role="radiogroup" aria-label="${escapeAttr(c.chooseColor || 'Choose a color')}">${swatchHtml}</div>` : ''}
        </div>
        <div class="elio-one-nudge__foot">
          ${choices.length ? `<p class="elio-one-nudge__math" data-math aria-live="polite"></p><button type="button" class="elio-one-nudge__add" data-add disabled>${escapeHtml(c.chooseColor || 'Choose a color')}</button>` : `<button type="button" class="elio-one-nudge__clone" data-clone data-variant="${cloneVariant}">${escapeHtml(c.addCta || 'Add one more')}</button>`}
          <button type="button" class="elio-one-nudge__skip" data-skip>${escapeHtml(c.skipCta || 'Checkout as is')}</button>
          <p class="elio-one-nudge__status" data-status aria-live="polite"></p>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';

    const close = () => { document.documentElement.style.overflow = ''; overlay.remove(); };
    const swatches = overlay.querySelector('.elio-one-nudge__swatches');
    const addBtn = overlay.querySelector('[data-add]');
    const mathEl = overlay.querySelector('[data-math]');
    const status = overlay.querySelector('[data-status]');

    overlay.querySelectorAll('[data-close]').forEach(el => el.addEventListener('click', close));
    overlay.querySelector('[data-skip]').addEventListener('click', () => { close(); goCheckout(); });
    document.addEventListener('keydown', function esc(e){ if(e.key !== 'Escape') return; document.removeEventListener('keydown', esc); if(document.body.contains(overlay)) close(); });

    overlay.querySelectorAll('.elio-one-nudge__swatch').forEach(sw => {
      sw.addEventListener('click', () => {
        overlay.querySelectorAll('.elio-one-nudge__swatch').forEach(o => o.setAttribute('aria-checked','false'));
        sw.setAttribute('aria-checked','true');
        selectedVariant = Number(sw.getAttribute('data-variant') || 0);
        selectedLabel = sw.getAttribute('data-label') || '';
        selectedPrice = Number(sw.getAttribute('data-price') || 0);

        const m = bundleMath(cartUnit, selectedPrice, currentFamily);
        if (mathEl) {
          mathEl.textContent = m
            ? (c.mathTwoFor || 'Two for [total]').replace('[total]', money(m.total, currency, locale))
              + ' · ' + (c.mathSave || 'Save [amount]').replace('[amount]', money(m.savings, currency, locale))
            : '';
        }
        // This button adds AND routes to checkout in one action, so it has to say so.
        // "Add Navy" reads like the sheet stays open and there's another step coming.
        if (addBtn) {
          addBtn.disabled = !selectedVariant;
          addBtn.textContent = (c.addCheckout || 'Add [color] and check out')
            .replace('[color]', selectedLabel || '')
            .replace(/\s{2,}/g, ' ')
            .trim();
        }
      });
    });

    async function addAndGo(id, button) {
      if (!id) { close(); goCheckout(); return; }
      if (swatches) swatches.classList.add('is-busy');
      const original = button ? button.textContent : '';
      if (button) { button.disabled = true; button.textContent = c.addingText || 'Adding…'; }
      status.textContent = '';
      const chosenProduct = products.find(p => (p.variants || []).some(v => Number(v.id) === Number(id)));
      const presaleValue = chosenProduct && chosenProduct.presale ? (chosenProduct.presaleShip || 'Ships by October 29') : (item?.properties?.Presale || '');
      try { await addVariant(id, presaleValue); close(); goCheckout(); }
      catch (e) {
        if (swatches) swatches.classList.remove('is-busy');
        if (button) { button.disabled = false; button.textContent = original; }
        status.textContent = c.errorText || 'Could not add that brief. Try again.';
      }
    }

    if (addBtn) addBtn.addEventListener('click', () => { if (selectedVariant) addAndGo(selectedVariant, addBtn); });
    const cloneBtn = overlay.querySelector('[data-clone]');
    if (cloneBtn) cloneBtn.addEventListener('click', () => addAndGo(Number(cloneBtn.getAttribute('data-variant') || 0), cloneBtn));

    requestAnimationFrame(() => {
      // Focus the close control, never a colour. Focusing a swatch puts a ring on it
      // that reads as a selection the customer never made.
      const preferred = overlay.querySelector('.elio-one-nudge__close') || cloneBtn || overlay.querySelector('[data-skip]');
      preferred?.focus();
    });
  }

  function escapeHtml(v){ return String(v ?? '').replace(/[&<>'"]/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[ch])); }
  function escapeAttr(v){ return escapeHtml(v); }

  /* ---------------------------------------------------------------
     Do not ask the same deliberate decision twice.

     If the shopper explicitly chose "Single" on the PDP, that IS the
     answer to "make it a pair?" -- re-asking at checkout is the same
     question a second time. The PDP writes a claim ticket; this reads it.

     Staleness is guarded three ways, because a marker that wrongly
     persists would silently disable the nudge for every later cart:
       1. It must name the exact variant now sitting alone in the cart.
       2. It expires (ELIO_SINGLE_TTL_MS).
       3. It is CONSUMED on use -- one explicit decision buys exactly one
          bypass, so it can never leak into a subsequent cart.
     A one-item cart built any other way (collection page, cart drawer,
     restored session) has no ticket and still gets the nudge.
     --------------------------------------------------------------- */
  const ELIO_SINGLE_KEY = 'elio:pdp-single-intent';
  const ELIO_SINGLE_TTL_MS = 6 * 60 * 60 * 1000;

  function readSingleIntent() {
    try {
      const raw = sessionStorage.getItem(ELIO_SINGLE_KEY);
      if (!raw) return null;
      const v = JSON.parse(raw);
      if (!v || !v.variantId || !v.ts) return null;
      if (Date.now() - Number(v.ts) > ELIO_SINGLE_TTL_MS) {
        clearSingleIntent();
        return null;
      }
      return v;
    } catch (e) { return null; }
  }

  function clearSingleIntent() {
    try { sessionStorage.removeItem(ELIO_SINGLE_KEY); } catch (e) {}
  }

  /* True only when the ticket matches THIS cart exactly: one line, one unit,
     that variant. Consumes the ticket either way so it cannot be reused. */
  function consumeSingleIntentFor(cart) {
    const intent = readSingleIntent();
    if (!intent) return false;
    clearSingleIntent();
    const items = (cart && cart.items) || [];
    if (Number(cart.item_count || 0) !== 1) return false;
    if (items.length !== 1) return false;
    const item = items[0];
    if (Number(item.quantity || 0) !== 1) return false;
    return Number(item.variant_id || item.id || 0) === Number(intent.variantId);
  }

  async function checkout(opts = {}) {
    if (busy) return;
    const c = config();
    const sizeGateApproved = !!opts.sizeGateApproved;
    if (bypassOnce || !c.enabled) { bypassOnce = false; goCheckout({ sizeGateApproved }); return; }
    busy = true;
    try {
      const cart = await getCart();
      /* Prompt at 1 and 3 only: a one-item cart, or one brief short of a
         second pair. Even counts are complete, and 5+ has demonstrated
         intent, so neither is interrupted. */
      const shouldShow = hasIncompletePair(cart) ||
        (c.testMode && Number(cart.item_count || 0) > 0);
      if (!shouldShow) { goCheckout({ sizeGateApproved }); return; }
      /* Test mode deliberately ignores the ticket so the nudge stays inspectable. */
      if (!c.testMode && consumeSingleIntentFor(cart)) { goCheckout({ sizeGateApproved }); return; }
      await showNudge(cart);
    } catch (e) {
      console.warn('[ELIO] one-item checkout nudge failed open', e);
      goCheckout({ sizeGateApproved });
    } finally { busy = false; }
  }

  function isCheckoutTrigger(target) {
    return !!target?.closest?.('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"], .js-checkout-button, [data-elio-checkout]');
  }
  function isCartForm(form) {
    if (!form) return false;
    const action = form.getAttribute('action') || '';
    return form.id === 'cartForm' || form.id === 'cart-drawer-form' || action.includes('/cart');
  }
  function activeIsCheckout() {
    const a = document.activeElement;
    return !!(a?.name === 'checkout' || a?.closest?.('button[name="checkout"],input[name="checkout"]'));
  }

  document.addEventListener('click', e => {
    if (!isCheckoutTrigger(e.target)) return;
    if (e.target.closest('.shopify-payment-button')) return;
    const c = config();
    if (!c.enabled) return;
    const trigger = e.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href$="/checkout"], .js-checkout-button, [data-elio-checkout]');
    const sizeGateApproved = !!(trigger && trigger.dataset && trigger.dataset.elioSizeGateApproved === '1');
    if (trigger && trigger.dataset) delete trigger.dataset.elioSizeGateApproved;
    e.preventDefault(); e.stopImmediatePropagation(); e.stopPropagation();
    checkout({ source: 'click', sizeGateApproved });
  }, true);

  document.addEventListener('submit', e => {
    if (!isCartForm(e.target) || !activeIsCheckout()) return;
    const c = config();
    if (!c.enabled) return;
    e.preventDefault(); e.stopImmediatePropagation(); e.stopPropagation();
    checkout({ source: 'submit' });
  }, true);

  window.elioCheckoutNudge = { checkout, goCheckout, getCart };
})();
